import * as React from 'react';
import classnames from 'classnames';
import { useEventListener } from '../hooks';
import Base, { TypeSizes } from '../Base/Base';
import Flex, { Props as FlexProps } from '../Flex/Flex';
import './Expand.css';

const { useEffect, useRef, useState } = React;

export interface Props extends FlexProps {
  /** @Ignore */
  children?: React.ReactNode;
  /** @Ignore */
  className?: string;
  /** Flag that controls the expanded state of the area. */
  expanded: boolean;
  /** @Ignore */
  padding?: TypeSizes;
  /** @Ignore */
  paddingHorizontal?: TypeSizes;
  /** @Ignore */
  paddingVertical?: TypeSizes;
  /** A CSS measurement of how much of the child content to always display when collapsed. */
  peekHeight?: string;
  /** Flag to control whether child content is rederend when collapsed. */
  removeChildren?: boolean;
}

const Expand: React.FunctionComponent<Props> = (props: Props) => {
  const {
    children,
    className,
    padding,
    paddingHorizontal,
    paddingVertical,
    removeChildren,
    expanded,
    peekHeight,
    ...rest
  } = props;

  const refOuter = useRef<HTMLElement>(null);
  const refInner = useRef<HTMLElement>(null);
  const refIsExpanded = useRef(expanded);
  const frameId = useRef<number | undefined>(undefined);

  const [height, setHeight] = useState<number | undefined>(expanded ? undefined : 0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [opacity, setOpacity] = useState<number | undefined>((expanded || peekHeight) ? 1 : 0);
  const [overflow, setOverflow] = useState<string | undefined>((expanded || peekHeight) ? undefined : 'hidden');

  useEventListener(refOuter.current, 'transitionend', () => {
    setIsTransitioning(false);

    if (expanded) {
      setHeight(undefined);
      setOpacity(undefined);
      setOverflow(undefined);
    }
  }, [expanded]);

  useEffect(() => {
    refIsExpanded.current = expanded;

    if (frameId.current) {
      window.cancelAnimationFrame(frameId.current);
      frameId.current = undefined;
    }

    if (expanded) {
      frameId.current = window.requestAnimationFrame(() => {
        if (refInner.current && height === 0) {
          setHeight(refInner.current.offsetHeight);
          setOpacity(1);
        }
      });
    } else {
      setIsTransitioning(true);
      setOverflow('hidden');
      frameId.current = window.requestAnimationFrame(() => {
        if (refInner.current && height === undefined) {
          setHeight(refInner.current.offsetHeight);
          frameId.current = window.requestAnimationFrame(() => {
            setHeight(0);

            if (!peekHeight) {
              setOpacity(0);
            }
          });
        }
      });
    }

    return () => {
      if (frameId.current) {
        window.cancelAnimationFrame(frameId.current);
        frameId.current = undefined;
      }
    };
  }, [expanded]);

  const renderChildren = isTransitioning || peekHeight || (expanded || refIsExpanded.current) || !removeChildren;
  const style = { opacity, overflow };
  const innerStyle = { overflow };
  const classes = classnames('Expand', {
    'Expand--expanded': expanded,
    'Expand--peeked': peekHeight,
  });

  const classesInner = classnames(className, 'Expand__inner');

  return (
    <Flex { ...rest }
        className={ classes }
        height={ height }
        innerRef={ refOuter }
        minHeight={ peekHeight }
        style={ style }>
      <Base
          className={ classesInner }
          innerRef={ refInner }
          padding={ padding }
          paddingHorizontal={ paddingHorizontal }
          paddingVertical={ paddingVertical }
          style={ innerStyle }>
        { renderChildren && children }
      </Base>
    </Flex>
  );
};

export default Expand;
